import React, { useState } from 'react';
import { Listing } from '../../../api/listings-client';
import FacilitiesStep from '../CreateListing/FacilitiesStep';
import PricingStep from '../CreateListing/PricingStep';
import TimeStep from '../CreateListing/TimeStep';
import { EditListingChanges } from './EditTypes';

interface DetailsEditContentProps {
    initialTab?: 'Facilities' | 'Pricing' | 'Time';
    listing: Listing,
    updateEditListing: (field: keyof EditListingChanges, value: any) => void;
}

const DetailsEditContent: React.FC<DetailsEditContentProps> = ({ 
    initialTab = 'Facilities',
    listing, 
    updateEditListing 
}) => {
    const [activeTab, setActiveTab] = useState(
        initialTab === 'Facilities' ? 1 : initialTab === 'Pricing' ? 2 : 3
    );
    const [localListing, setLocalListing] = useState(listing);

    const tabs = [
        { id: 1, name: 'Facilities' },
        { id: 2, name: 'Pricing' },
        { id: 3, name: 'Time' },
    ];

    const buttonClasses = (tabId: number) =>
        `w-full flex justify-center items-center text-start p-3 rounded-xl transition-colors duration-200 ${
            activeTab === tabId
                ? 'bg-gray-100 text-[#2476f2] hover:bg-gray-200'
                : 'text-gray-800 hover:bg-gray-100'
        } text-base font-medium`;

    const updateLocalAndParent = (field: keyof EditListingChanges, value: any) => {
        setLocalListing(prev => ({ ...prev, [field]: value }));
        updateEditListing(field, value);
    };

    const renderActiveComponent = () => {
        switch (activeTab) {
            case 1:
                return (
                    <FacilitiesStep
                        parking={localListing.parking}
                        setParking={(value) => updateLocalAndParent('parking', value)}
                        bathrooms={localListing.bathroom}
                        setBathrooms={(value) => updateLocalAndParent('bathroom', value)}
                        furnished={localListing.furnished}
                        setFurnished={(value) => updateLocalAndParent('furnished', value)}
                        editMode={true}
                    />
                );
            case 2:
                return (
                    <PricingStep
                        rent={localListing.rent}
                        setRent={(value) => updateLocalAndParent('rent', value)}
                        bond={localListing.bond}
                        setBond={(value) => updateLocalAndParent('bond', value)}
                        billsIncluded={localListing.billsIncluded}
                        setBillsIncluded={(value) => updateLocalAndParent('billsIncluded', value)}
                        editMode={true}
                    />
                );
            case 3:
                return (
                    <TimeStep
                        availableFrom={localListing.availableFrom}
                        setAvailableFrom={(value) => updateLocalAndParent('availableFrom', value)}
                        minimumStay={localListing.minimumStay}
                        setMinimumStay={(value) => updateLocalAndParent('minimumStay', value)}
                        maximumStay={localListing.maximumStay}
                        setMaximumStay={(value) => updateLocalAndParent('maximumStay', value)}
                        editMode={true}
                    />
                );
            default:
                return null;
        }
    };
    return (
        <div className="min-h-[450px]">
            <nav className="max-w-2xl mx-auto flex gap-x-4 font-medium" aria-label="Tabs" role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        className={buttonClasses(tab.id)}
                        onClick={() => setActiveTab(tab.id)}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        aria-controls={`tabs-content-${tab.id}`}
                    >
                        {tab.name}
                    </button>
                ))}
            </nav>
            <div className="py-6" role="tabpanel" id={`tabs-content-${activeTab}`} aria-labelledby={`tabs-${activeTab}`}>
                {renderActiveComponent()}
            </div>
        </div>
    );
};

export default DetailsEditContent;